import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaPlus, FaGripLines, FaExpand, FaCompress, FaFilePdf, FaFileExcel } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { exportGanttPDF, exportGanttExcel } from '../utils/ganttExport';

// ============================================
// PROJECT GANTT — custom, dependency-free
// · phases group the rows (Transport, Rigging …)
// · bars span each task's start → end date
// · DRAG the bar to move it, drag an edge to shorten/extend
// ============================================

const DAY_W = { day: 36, week: 14, month: 6 };
// NORMAL screen: how many days sit in the view window at each zoom. The window
// is always stretched to fit the pane, so there is NO horizontal scrolling and
// the page/tab scrolls VERTICALLY — exactly as before.
const ZOOM_SPAN = { day: 20, week: 60, month: 180 };
// FULL SCREEN only: the WHOLE project is rendered and the calendar is scrolled
// sideways to move through it.
const PRIORITIES = [
  { k: 'urgent', label: 'Urgent', color: '#EF4444' },
  { k: 'high', label: 'High', color: '#F97316' },
  { k: 'moderate', label: 'Moderate', color: '#EAB308' },
  { k: 'low', label: 'Low', color: '#3B82F6' }
];
const priorityOf = (t) => PRIORITIES.find((p) => p.k === (t.priority || 'moderate')) || PRIORITIES[2];
// Bar colours. "Not started" is brand NAVY — a grey bar reads as "nothing here",
// navy reads as "planned, not begun yet".
const STATUS_LEGEND = [
  { label: 'Not started', color: '#0A1929' },
  { label: 'In progress', color: '#2563EB' },
  { label: 'Done', color: '#00A86B' },
  { label: 'Blocked', color: '#EF4444' }
];
const STATUS_STYLE = {
  not_started: { bar: '#0A1929' },
  in_progress: { bar: '#2563EB' },
  done: { bar: '#00A86B' },
  blocked: { bar: '#EF4444' }
};
// What the bars/diamonds are coloured BY (the user's choice, remembered).
// Default = One Purpose green (the brand bar colour).
const BRAND_GREEN = '#00A86B';
const COLOR_MODES = [
  { k: 'brand', label: 'One Purpose', hint: 'the One Purpose green' },
  { k: 'status', label: 'Status' },
  { k: 'priority', label: 'Priority' },
  { k: 'phase', label: 'Phase' }
];
const PHASE_COLORS = ['#0A1929', '#0E7490', '#7C3AED', '#B45309', '#0F766E', '#BE123C', '#4338CA', '#4D7C0F'];
const NO_PHASE_COLOR = '#64748B';
// White text on dark fills, navy text on light ones (e.g. moderate yellow).
const textOn = (hex) => {
  const h = String(hex || '').replace('#', '');
  if (h.length < 6) return '#FFFFFF';
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.62 ? '#0A1929' : '#FFFFFF';
};

const toDate = (s) => (s ? new Date(`${String(s).slice(0, 10)}T00:00:00Z`) : null);
const fmt = (d) => (d ? d.toISOString().slice(0, 10) : null);
const addDays = (s, n) => {
  const d = toDate(s);
  if (!d) return s;
  d.setUTCDate(d.getUTCDate() + n);
  return fmt(d);
};
const diffDays = (a, b) => Math.round((toDate(b) - toDate(a)) / 86400000);
const pretty = (s) => (s ? toDate(s).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' }) : '—');

function ProjectGantt({ phases = [], tasks = [], onOpenTask, onReschedule, onAddTask, canEditTask, fill = false, onMoveTask, onDeletePhase, onMovePhase, project = null }) {
  // 'fit' scales the WHOLE project to the width of the screen.
  const [zoom, setZoom] = useState('day');
  const [fullscreen, setFullscreen] = useState(false);
  const [boxW, setBoxW] = useState(0);
  const dragRef = useRef(null);
  const ghostRef = useRef(null);
  const movedRef = useRef(false);
  const scrollRef = useRef(null);
  const [ghost, setGhost] = useState(null);
  // What the bars & diamonds are coloured by (remembered between visits).
  const [colorBy, setColorBy] = useState(() => {
    const saved = window.localStorage?.getItem('ganttColorBy');
    return COLOR_MODES.some((m) => m.k === saved) ? saved : 'brand';
  });
  // Left edge of the NORMAL-screen view window (YYYY-MM-DD); null = auto.
  // Ignored in full screen, which shows the whole project.
  const [windowStart, setWindowStart] = useState(null);

  // ── Freeze pane: the column + date headers stay visible while rows scroll.
  // Their sticky offset = toolbar height + colour-key height (measured, because
  // the key wraps on narrow screens).
  const toolbarRef = useRef(null);
  const keyRef = useRef(null);
  const headScrollRef = useRef(null); // frozen date header — mirrored to the body
  const proxyRef = useRef(null);      // pinned bottom scrollbar — mirrored to the body
  const dragPhaseRef = useRef(null);  // id of the phase row currently being dragged
  const [headTop, setHeadTop] = useState(80);

  // Keep the frozen date header AND the pinned bottom scrollbar in step with the
  // rows' horizontal scroll.
  const syncHeaderScroll = (e) => {
    const h = headScrollRef.current;
    if (h) h.scrollLeft = e.currentTarget.scrollLeft;
    const p = proxyRef.current;
    if (p && Math.abs(p.scrollLeft - e.currentTarget.scrollLeft) > 1) p.scrollLeft = e.currentTarget.scrollLeft;
  };

  useEffect(() => { ghostRef.current = ghost; }, [ghost]);

  const todayStr = useMemo(() => fmt(new Date()), []);

  // Measure the timeline viewport so "Fit" can scale the project to it.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => setBoxW(el.clientWidth);
    measure();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [fullscreen]);

  // Everything the schedule touches — defines how far you can pan.
  const bounds = useMemo(() => {
    const dates = [];
    tasks.forEach((t) => { if (t.start_date) dates.push(t.start_date); if (t.end_date) dates.push(t.end_date); });
    phases.forEach((p) => { if (p.start_date) dates.push(p.start_date); if (p.end_date) dates.push(p.end_date); });
    if (!dates.length) return { min: addDays(todayStr, -7), max: addDays(todayStr, 30) };
    const min = dates.reduce((a, b) => (a < b ? a : b));
    const max = dates.reduce((a, b) => (a > b ? a : b));
    return {
      min: addDays(min < todayStr ? min : todayStr, -7),
      max: addDays(max > todayStr ? max : todayStr, 30)
    };
  }, [tasks, phases, todayStr]);

  // ── Timeline ──────────────────────────────────────────────────────────
  // The WHOLE project is always rendered, in normal and full screen. Day/Week/
  // Month change the zoom (pixels per day) and you scroll sideways to browse;
  // 'fit' squeezes the whole project into the pane so nothing scrolls.
  // (Before: normal view rendered a fixed 'window' of days stretched to fill
  // the pane, so the horizontal scrollbar had nothing to scroll.)
  const projectDays = Math.max(diffDays(bounds.min, bounds.max) + 1, 10);
  const span = projectDays;
  const dayW = zoom === 'fit'
    ? (boxW > 0 ? Math.max(boxW / projectDays, 1) : 6)
    : DAY_W[zoom];

  // Always start at the beginning of the project, like the printed PDF.
  const winStart = useMemo(() => bounds.min, [bounds]);

  const range = useMemo(() => {
    const days = [];
    for (let i = 0; i < span; i++) days.push(addDays(winStart, i));
    return { start: winStart, end: addDays(winStart, span - 1), days, todayStr };
  }, [winStart, span, todayStr]);

  // ◀ ▶ scroll the calendar sideways by ~70% of the visible timeline (the whole
  // project is rendered in both modes now, so this is the same either way).
  const scrollCalendar = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const step = Math.max(el.clientWidth * 0.7, 200);
    if (el.scrollBy) el.scrollBy({ left: dir * step, behavior: 'smooth' });
    else el.scrollLeft += dir * step;
  };

  const stepBack = () => scrollCalendar(-1);
  const stepForward = () => scrollCalendar(1);
  const jumpToday = () => {
    const el = scrollRef.current;
    if (!el) return;
    const x = diffDays(range.start, todayStr) * dayW - el.clientWidth * 0.35;
    el.scrollLeft = Math.max(x, 0);
  };

  // Normal screen: keep "today" in view when the window or zoom changes.
  useEffect(() => {
    if (fullscreen) return;
    const el = scrollRef.current;
    if (!el) return;
    const x = diffDays(range.start, range.todayStr) * dayW - el.clientWidth * 0.4;
    el.scrollLeft = Math.max(x, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, winStart, dayW, fullscreen]);

  // Full screen opens at the START of the project (scrollLeft 0) so the whole
  // calendar reads left-to-right, like the printed PDF.

  // Remember the colour choice.
  useEffect(() => {
    try { window.localStorage.setItem('ganttColorBy', colorBy); } catch (err) { /* ignore */ }
  }, [colorBy]);

  // Measure the sticky offset for the frozen header (toolbar + colour key).
  useEffect(() => {
    const measure = () => {
      const t = toolbarRef.current?.offsetHeight || 52;
      const k = keyRef.current?.offsetHeight || 30;
      setHeadTop(t + k);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [colorBy, fullscreen, zoom, tasks.length, phases.length]);

  // Full screen: Escape to exit + lock the page behind it.
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => { if (e.key === 'Escape') setFullscreen(false); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [fullscreen]);

  const totalW = range.days.length * dayW;
  const xFor = (dateStr) => diffDays(range.start, dateStr) * dayW;

  // ---- drag handling (move + resize) ----
  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const delta = Math.round((e.clientX - d.startX) / d.dayW);
      if (delta !== 0) movedRef.current = true;
      setGhost({ taskId: d.task.id, deltaDays: delta, mode: d.mode });
    };
    const onUp = () => {
      const d = dragRef.current;
      dragRef.current = null;
      if (!d) return;
      const delta = ghostRef.current?.deltaDays || 0;
      setGhost(null);
      if (!movedRef.current || delta === 0) { onOpenTask?.(d.task); return; }
      let s = d.task.start_date;
      let en = d.task.end_date;
      if (d.mode === 'move') { s = addDays(s, delta); en = addDays(en, delta); }
      else if (d.mode === 'left') { s = addDays(s, delta); if (diffDays(s, en) < 0) s = en; }
      else if (d.mode === 'right') { en = addDays(en, delta); if (diffDays(s, en) < 0) en = s; }
      onReschedule?.(d.task, s, en);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [onOpenTask, onReschedule, dayW]);

  const startDrag = (e, task, mode) => {
    if (mode !== 'move' && canEditTask && !canEditTask(task)) return;
    if (!task.start_date) { onOpenTask?.(task); return; }
    e.preventDefault();
    e.stopPropagation();
    movedRef.current = false;
    dragRef.current = { task, mode, startX: e.clientX, dayW };
  };

  // ── Drag & drop (left column): move a task between phases or re-order it ──
  const [dragTask, setDragTask] = useState(null);
  const [dropTarget, setDropTarget] = useState(null); // { type: 'task' | 'phase', id }
  const [exporting, setExporting] = useState('');

  // ── draggable left column (spreadsheet-style) ──
  const [leftW, setLeftW] = useState(() => {
    const saved = Number(window.localStorage?.getItem('ganttLeftW'));
    return saved >= 240 && saved <= 900 ? saved : 520;
  });
  const colResizing = useRef(false);

  useEffect(() => {
    const onMove = (e) => {
      if (!colResizing.current) return;
      const w = Math.min(Math.max(colResizing.current.startW + (e.clientX - colResizing.current.startX), 240), 900);
      setLeftW(w);
    };
    const onUp = () => {
      if (!colResizing.current) return;
      colResizing.current = false;
      document.body.style.cursor = '';
      setLeftW((w) => {
        try { window.localStorage.setItem('ganttLeftW', String(Math.round(w))); } catch (err) { /* ignore */ }
        return w;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const startColResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    colResizing.current = { startX: e.clientX, startW: leftW };
    document.body.style.cursor = 'col-resize';
  };

  const doExport = async (kind) => {
    setExporting(kind);
    try {
      const payload = { project: project || {}, phases, tasks, colorBy };
      if (kind === 'pdf') {
        await exportGanttPDF(payload);
        toast.success('📄 Schedule PDF downloaded');
      } else {
        await exportGanttExcel(payload);
        toast.success('📊 Schedule downloaded for Excel');
      }
    } catch (e) {
      console.error('Gantt export failed:', e);
      toast.error('Export failed: ' + (e.message || 'unknown error'));
    } finally {
      setExporting('');
    }
  };
  const dropTask = (target) => {
    if (dragTask && onMoveTask) onMoveTask(dragTask, target);
    setDragTask(null);
    setDropTarget(null);
  };
  const canDrag = (t) => !canEditTask || canEditTask(t);

  // Who is this task allocated to? (a person, or a company like Transcor)
  const ownerLabel = (t) => {
    const a = t.assignees || [];
    if (!a.length) return '—';
    const first = a[0].name || a[0].email || 'Unnamed';
    return a.length > 1 ? `${first} +${a.length - 1}` : first;
  };

  const groups = useMemo(() => {
    const out = [];
    const byPhase = {};
    tasks.forEach((t) => {
      const key = t.phase_id || '__none';
      byPhase[key] = byPhase[key] || [];
      byPhase[key].push(t);
    });
    phases.forEach((p) => {
      // Show EVERY phase, even one with no tasks yet — it gets its own h-11 row on
      // BOTH sides of the split (left column + timeline), so bar alignment is kept.
      // Otherwise a phase you just created is invisible until it has a task.
      out.push({ id: p.id, name: p.name, tasks: byPhase[p.id] || [] });
    });
    if (byPhase.__none?.length) out.push({ id: '__none', name: 'General', tasks: byPhase.__none });
    return out;
  }, [tasks, phases]);

  // Stable colour per phase (by the order they're listed) — used when the user
  // colours the bars BY PHASE.
  const phaseColorMap = useMemo(() => {
    const m = {};
    phases.forEach((p, i) => { m[p.id] = PHASE_COLORS[i % PHASE_COLORS.length]; });
    return m;
  }, [phases]);

  // What colour does THIS task's bar/diamond get, given the chosen colour mode?
  const colorFor = (t) => {
    if (colorBy === 'brand') return BRAND_GREEN;
    if (colorBy === 'priority') return priorityOf(t).color;
    if (colorBy === 'phase') return phaseColorMap[t.phase_id] || NO_PHASE_COLOR;
    return (STATUS_STYLE[t.status] || STATUS_STYLE.not_started).bar;
  };

  const renderBar = (task) => {
    const g = ghost && ghost.taskId === task.id ? ghost : null;
    const delta = g ? g.deltaDays : 0;
    let s = task.start_date;
    let en = task.end_date;
    if (g && s && en) {
      if (g.mode === 'move') { s = addDays(s, delta); en = addDays(en, delta); }
      else if (g.mode === 'left') { s = addDays(s, delta); if (diffDays(s, en) < 0) s = en; }
      else if (g.mode === 'right') { en = addDays(en, delta); if (diffDays(s, en) < 0) en = s; }
    }
    if (!s && !en) {
      return (
        <div className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-gray-400">
          <button onClick={() => onOpenTask?.(task)} className="hover:text-green underline">set dates</button>
        </div>
      );
    }

    // Bars & diamonds are coloured by the chosen mode (Status · Priority · Phase).
    const fill = colorFor(task);
    const ink = textOn(fill);
    const draggable = !canEditTask || canEditTask(task);

    // ── Milestone: exactly one date (a "by this date" item) → a DIAMOND marker ──
    const onlyStart = s && !en;
    const onlyEnd = !s && en;
    if (onlyStart || onlyEnd) {
      const at = s || en;
      if (at < range.start || at > range.end) {
        const later = at > range.end;
        return (
          <div
            className="absolute top-1/2 -translate-y-1/2 text-[10px] text-gray-300"
            style={{ left: later ? totalW - 24 : 4 }}
            title={`${task.name} · milestone ${pretty(at)} — outside this window`}
          >{later ? '▶' : '◀'}</div>
        );
      }
      const cx = xFor(at) + dayW / 2;
      return (
        <div
          className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 rotate-45 rounded-[3px] shadow-sm border ${draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`}
          style={{ left: cx - 8, backgroundColor: fill, borderColor: '#fff', opacity: g ? 0.85 : 1 }}
          onMouseDown={(e) => startDrag(e, task, 'move')}
          title={`${task.name} · milestone (by ${pretty(at)})`}
        />
      );
    }
    if (en < range.start || s > range.end) {
      const later = s > range.end;
      return (
        <div
          className="absolute top-1/2 -translate-y-1/2 text-[10px] text-gray-300"
          style={{ left: later ? totalW - 24 : 4 }}
          title={`${task.name} · ${pretty(s)} → ${pretty(en)} — outside this window`}
        >
          {later ? '▶' : '◀'}
        </div>
      );
    }
    const visStart = s < range.start ? range.start : s;
    const visEnd = en > range.end ? range.end : en;
    const left = xFor(visStart);
    const width = Math.max((diffDays(visStart, visEnd) + 1) * dayW, 12);
    const clippedLeft = s < range.start;
    const clippedRight = en > range.end;
    return (
      <div
        className={`absolute top-1/2 -translate-y-1/2 h-7 rounded-md shadow-sm flex items-center px-2 ${clippedLeft ? 'rounded-l-none' : ''} ${clippedRight ? 'rounded-r-none' : ''} ${draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`}
        style={{ left, width, backgroundColor: fill, opacity: g ? 0.85 : 1, boxShadow: task.priority === 'urgent' ? '0 0 0 2px #EF4444' : undefined }}
        onMouseDown={(e) => startDrag(e, task, 'move')}
        title={`${task.name} · ${pretty(s)} → ${pretty(en)}`}
      >
        {draggable && (
          <div className="absolute -left-1 top-0 h-full w-2 cursor-ew-resize" onMouseDown={(e) => startDrag(e, task, 'left')} />
        )}
        <span className="text-[11px] font-medium truncate select-none" style={{ color: ink }}>{task.name}</span>
        {draggable && (
          <div className="absolute -right-1 top-0 h-full w-2 cursor-ew-resize" onMouseDown={(e) => startDrag(e, task, 'right')} />
        )}
      </div>
    );
  };

  // ── the date ruler (month strip + day strip) — rendered in the FROZEN header
  const calendarHeader = (
    <>
      <div className="h-7 border-b relative" style={{ borderColor: '#E8ECEF' }}>
        {range.days.map((d, i) => {
          const dt = toDate(d);
          const show = dt.getUTCDate() === 1 || i === 0;
          return show ? (
            <div key={`m${d}`} className="absolute text-[11px] font-semibold text-gray-500" style={{ left: i * dayW + 4 }}>
              {dt.toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' })}
            </div>
          ) : null;
        })}
      </div>
      <div className="h-7 flex border-b" style={{ borderColor: '#E8ECEF' }}>
        {range.days.map((d) => {
          const dt = toDate(d);
          const isToday = d === range.todayStr;
          return (
            <div
              key={d}
              className={`shrink-0 text-[10px] text-center leading-7 ${isToday ? 'font-bold text-green' : 'text-gray-400'}`}
              style={{ width: dayW, borderRight: '1px solid #F1F5F9', backgroundColor: isToday ? 'rgba(0,168,107,0.08)' : undefined }}
            >
              {dayW >= 16 || dt.getUTCDay() === 1 ? dt.getUTCDate() : ''}
            </div>
          );
        })}
      </div>
    </>
  );

  return (
    <div
      className={
        fullscreen
          ? 'fixed inset-0 z-30 bg-white flex flex-col min-w-0 max-w-full'
          : (fill
            ? 'bg-white rounded-2xl border flex flex-col flex-1 min-h-0 w-full max-w-full min-w-0'
            : 'bg-white rounded-2xl border w-full max-w-full min-w-0')
      }
      style={fullscreen ? {} : { borderColor: '#E8ECEF' }}
    >
      <div ref={toolbarRef} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-3 py-2 border-b shrink-0 sticky top-0 z-20 bg-white rounded-t-2xl min-w-0 max-w-full" style={{ borderColor: '#E8ECEF' }}>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 flex-1 min-w-0 justify-between">
          <div className="flex items-center gap-1">
            <button
              onClick={stepBack}
              title={fullscreen ? 'Scroll to earlier dates' : 'Earlier'}
              className="w-8 h-8 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 text-xs font-medium"
            >◀</button>
            <button
              onClick={jumpToday}
              className="px-3 h-8 rounded-lg bg-navy text-white hover:opacity-90 text-xs font-semibold"
            >Today</button>
            <button
              onClick={stepForward}
              title={fullscreen ? 'Scroll to later dates' : 'Later'}
              className="w-8 h-8 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 text-xs font-medium"
            >▶</button>
          </div>
          <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
            {pretty(range.start)} – {pretty(range.end)}
            {fullscreen || zoom === 'fit' ? ` · whole project · ${projectDays} days` : ''}
          </span>
          <span className="hidden 2xl:inline text-xs text-gray-400 flex items-center gap-1 whitespace-nowrap">
            <FaGripLines className="text-gray-300" /> {fullscreen
              ? 'Scroll sideways (shift-wheel) · click a task to open it'
              : 'Drag bars to change dates · drag ROWS to re-order'}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0 justify-between">
          <div className="flex items-center gap-1.5 w-full justify-end">
          <button
            onClick={() => doExport('excel')}
            disabled={!!exporting}
            title="Download for Excel"
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors flex items-center gap-1 disabled:opacity-50"
          >
            <FaFileExcel size={11} /> <span className="hidden sm:inline">Excel</span>
          </button>
          <button
            onClick={() => doExport('pdf')}
            disabled={!!exporting}
            title="Download PDF (landscape, logo + watermark)"
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors flex items-center gap-1 disabled:opacity-50"
          >
            <FaFilePdf size={11} /> <span className="hidden sm:inline">PDF</span>
          </button>
          <button
            onClick={() => setFullscreen((f) => !f)}
            title={fullscreen ? 'Exit full screen (Esc)' : 'Full screen'}
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors flex items-center gap-1"
          >
            {fullscreen ? <FaCompress size={11} /> : <FaExpand size={11} />}
            <span className="hidden sm:inline">{fullscreen ? 'Exit' : 'Full screen'}</span>
          </button>
          </div>
          <span className="hidden 2xl:inline text-[10px] font-semibold uppercase tracking-wide text-gray-400">Colour</span>
          {COLOR_MODES.map((m) => (
            <button
              key={m.k}
              onClick={() => setColorBy(m.k)}
              title={`Colour the bars & diamonds by ${m.hint || m.label.toLowerCase()}`}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${colorBy === m.k ? 'bg-green text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {m.label}
            </button>
          ))}
          <span className="w-px h-5 bg-gray-200 mx-1" />
          {[
            { k: 'fit', label: 'Fit' },
            { k: 'day', label: 'Day' },
            { k: 'week', label: 'Week' },
            { k: 'month', label: 'Month' }
          ].map(({ k, label }) => (
            <button
              key={k}
              onClick={() => setZoom(k)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${zoom === k ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {label}
            </button>
          ))}
          {onAddTask && (
            <button
              onClick={() => onAddTask(null)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-green text-white hover:bg-navy transition-colors flex items-center gap-1"
            >
              <FaPlus size={10} /> Task
            </button>
          )}
        </div>
      </div>

      {/* ── PINNED HORIZONTAL SCROLLBAR ─────────────────────────────────────
          Stays at the bottom of the SCREEN (normal + full screen) so you can
          scroll sideways across the project from any vertical position — you no
          longer have to scroll down to the end of the calendar to reach it.
          It mirrors the timeline: dragging it scrolls the calendar, and
          scrolling the calendar moves it. */}
      <div
        className="fixed bottom-0 right-0 z-30 bg-white border-t"
        style={{ left: leftW + (fullscreen ? 0 : 256), borderColor: '#E8ECEF', height: 16 }}
      >
        <div
          ref={proxyRef}
          onScroll={(e) => {
            const el = scrollRef.current;
            if (el && Math.abs(el.scrollLeft - e.currentTarget.scrollLeft) > 1) {
              el.scrollLeft = e.currentTarget.scrollLeft;
              const h = headScrollRef.current;
              if (h) h.scrollLeft = e.currentTarget.scrollLeft;
            }
          }}
          className="w-full h-full overflow-x-scroll"
        >
          <div style={{ width: totalW, height: 1 }} />
        </div>
      </div>

      {/* Colour key — what the bar colours and priority chips mean */}
      <div ref={keyRef} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 border-b bg-gray-50 text-[11px] text-gray-600 shrink-0 sticky top-[52px] z-10" style={{ borderColor: '#E8ECEF' }}>
        <span className="font-semibold uppercase tracking-wide text-gray-400">
          {colorBy === 'brand' ? 'Bar colour' : colorBy === 'status' ? 'Status' : colorBy === 'priority' ? 'Priority (bars)' : 'Phase (bars)'}
        </span>
        {colorBy === 'phase'
          ? (
            <>
              {phases.map((p, i) => (
                <span key={p.id} className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded" style={{ backgroundColor: PHASE_COLORS[i % PHASE_COLORS.length] }} />
                  {p.name}
                </span>
              ))}
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded" style={{ backgroundColor: NO_PHASE_COLOR }} />
                No phase
              </span>
            </>
          )
          : colorBy === 'brand'
            ? (
              <>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded" style={{ backgroundColor: BRAND_GREEN }} />
                  One Purpose green — every bar &amp; ◆ marker
                </span>
                <span className="text-gray-400 italic">switch to Status / Priority / Phase to colour-code them</span>
              </>
            )
            : (colorBy === 'priority' ? PRIORITIES : STATUS_LEGEND).map((s) => (
              <span key={s.label || s.k} className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded" style={{ backgroundColor: s.color }} />
                {s.label}
              </span>
            ))}
        {colorBy !== 'priority' && (
          <>
            <span className="font-semibold uppercase tracking-wide text-gray-400 ml-2">Priority</span>
            {PRIORITIES.map((p) => (
              <span key={p.k} className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }} />
                {p.label}
              </span>
            ))}
          </>
        )}
        <span className="font-semibold uppercase tracking-wide text-gray-400 ml-2">Marker</span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rotate-45 rounded-[2px]" style={{ backgroundColor: colorBy === 'brand' ? BRAND_GREEN : colorBy === 'priority' ? PRIORITIES[2].color : (colorBy === 'phase' ? PHASE_COLORS[0] : '#0A1929') }} />
          By this date (single date) — same colour rules
        </span>
      </div>

      {/* ── FROZEN HEADERS (freeze pane) — the column titles + date ruler stay
             in place while the rows scroll down, and only move sideways with
             the grid (horizontal scroll is mirrored from the rows below). ── */}
      <div className="shrink-0 bg-white sticky z-10" style={{ top: headTop }}>
        <div className="flex">
          <div className="shrink-0 border-r relative" style={{ borderColor: '#E8ECEF', width: leftW }}>
            <div className="h-14 border-b grid grid-cols-[1fr_120px_86px] items-center px-3 text-[11px] font-bold uppercase tracking-wide text-gray-500" style={{ borderColor: '#E8ECEF' }}>
              <span>Phase / Task</span>
              <span>Owner</span>
              <span>Priority</span>
            </div>
            {/* drag to widen the task column */}
            <div
              onMouseDown={startColResize}
              onDoubleClick={() => setLeftW(440)}
              title="Drag to widen the task column · double-click to reset"
              className="absolute top-0 -right-1 h-full w-2 cursor-col-resize z-30 group"
            >
              <div className="absolute right-1 top-0 h-full w-[2px] bg-transparent group-hover:bg-green/60" />
            </div>
          </div>
          <div ref={headScrollRef} className="flex-1 min-w-0 overflow-hidden">
            <div style={{ width: totalW, minWidth: '100%' }}>{calendarHeader}</div>
          </div>
        </div>
      </div>

      {/* ── SCROLLING ROWS ── */}
      <div className={`min-w-0 w-full max-w-full ${(fullscreen || fill) ? 'flex-1 min-h-0 overflow-y-auto overflow-x-hidden' : ''}`}>
      <div className="flex min-w-0 w-full max-w-full">
        <div className="shrink-0 border-r relative" style={{ borderColor: '#E8ECEF', width: leftW }}>
          <div
            onMouseDown={startColResize}
            onDoubleClick={() => setLeftW(440)}
            title="Drag to widen the task column · double-click to reset"
            className="absolute top-0 -right-1 h-full w-2 cursor-col-resize z-10 group"
          >
            <div className="absolute right-1 top-0 h-full w-[2px] bg-transparent group-hover:bg-green/60" />
          </div>
          {groups.length === 0 && (
            <div className="p-4 text-sm text-gray-400">No tasks yet — add a phase or a task to start scheduling.</div>
          )}
          {groups.map((grp) => (
            <div key={grp.id}>
              <div
                draggable={!!onMovePhase && grp.id !== '__none'}
                onDragStart={(e) => { if (grp.id === '__none') return; dragPhaseRef.current = grp.id; e.dataTransfer.effectAllowed = 'move'; }}
                onDragEnd={() => { dragPhaseRef.current = null; setDropTarget(null); }}
                className={`h-11 flex items-center gap-2 px-3 bg-gray-50 border-b text-xs font-semibold text-navy ${(dropTarget?.type === 'phase' || dropTarget?.type === 'phase-order') && dropTarget.id === grp.id ? 'ring-2 ring-green ring-inset bg-green/5' : ''}`}
                style={{ borderColor: '#E8ECEF' }}
                onDragOver={(e) => {
                  if (dragPhaseRef.current) {
                    if (dragPhaseRef.current !== grp.id) { e.preventDefault(); setDropTarget({ type: 'phase-order', id: grp.id }); }
                  } else if (dragTask) { e.preventDefault(); setDropTarget({ type: 'phase', id: grp.id }); }
                }}
                onDragLeave={() => setDropTarget((d) => ((d?.type === 'phase' || d?.type === 'phase-order') && d.id === grp.id ? null : d))}
                onDrop={(e) => {
                  e.preventDefault();
                  const movingPhase = dragPhaseRef.current;
                  dragPhaseRef.current = null;
                  setDropTarget(null);
                  if (movingPhase) { if (onMovePhase) onMovePhase(movingPhase, grp.id); return; }
                  dropTask({ phase_id: grp.id === '__none' ? '' : grp.id, before_task_id: null });
                }}
              >
                {onMovePhase && grp.id !== '__none' && (
                  <span className="text-gray-300 cursor-grab select-none shrink-0" title="Drag to reorder this phase">⠿</span>
                )}
                <span className="truncate">{grp.name}</span>
                <div className="ml-auto flex items-center gap-2 shrink-0">
                  {onAddTask && (
                    <button onClick={() => onAddTask(grp.id === '__none' ? null : grp.id)} title="Add task to this phase" className="text-gray-400 hover:text-green">
                      <FaPlus size={10} />
                    </button>
                  )}
                  {onDeletePhase && grp.id !== '__none' && (
                    <button onClick={() => onDeletePhase(grp)} title="Delete this phase" className="px-1.5 rounded text-gray-400 hover:text-white hover:bg-red-500">
                      ✕
                    </button>
                  )}
                </div>
              </div>
              {grp.tasks.map((t) => (
                <div
                  key={t.id}
                  draggable={canDrag(t)}
                  onDragStart={(e) => { setDragTask(t); e.dataTransfer.effectAllowed = 'move'; }}
                  onDragEnd={() => { setDragTask(null); setDropTarget(null); }}
                  onDragOver={(e) => { if (dragTask && dragTask.id !== t.id) { e.preventDefault(); setDropTarget({ type: 'task', id: t.id }); } }}
                  onDragLeave={() => setDropTarget((d) => (d?.type === 'task' && d.id === t.id ? null : d))}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragTask && dragTask.id !== t.id) dropTask({ phase_id: t.phase_id || '', before_task_id: t.id });
                  }}
                  className={`h-11 grid grid-cols-[1fr_120px_86px] items-center gap-2 px-3 border-b text-sm hover:bg-gray-50 cursor-pointer ${dropTarget?.type === 'task' && dropTarget.id === t.id ? 'border-t-2 border-green bg-green/5' : ''} ${dragTask?.id === t.id ? 'opacity-40' : ''}`}
                  style={{ borderColor: '#F1F5F9' }}
                  onClick={() => onOpenTask?.(t)}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: colorFor(t) }} title={COLOR_MODES.find((m) => m.k === colorBy)?.label} />
                    <span className="truncate text-gray-700" title={t.name}>{t.name}</span>
                  </div>
                  <span className="truncate text-xs text-gray-500" title={ownerLabel(t)}>{ownerLabel(t)}</span>
                  <span>
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                      style={{ backgroundColor: `${priorityOf(t).color}22`, color: priorityOf(t).color }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: priorityOf(t).color }} />
                      {priorityOf(t).label}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div ref={scrollRef} onScroll={syncHeaderScroll} className="flex-1 min-w-0 overflow-x-auto">
          <div style={{ width: totalW, minWidth: '100%' }}>
            <div className="relative">
              {range.days.map((d, i) => (
                <div
                  key={`g${d}`}
                  className="absolute top-0 bottom-0"
                  style={{ left: i * dayW, width: dayW, borderRight: '1px solid #F8FAFC', backgroundColor: d === range.todayStr ? 'rgba(0,168,107,0.06)' : undefined }}
                />
              ))}
              {groups.map((grp) => (
                <div key={grp.id}>
                  <div className="h-11 bg-gray-50 border-b" style={{ borderColor: '#E8ECEF' }}>
                    <div className="relative h-full">
                      <div className="absolute top-1/2 -translate-y-1/2 text-[11px] font-semibold text-gray-400 pl-2">
                        {grp.name} ({grp.tasks.length})
                      </div>
                    </div>
                  </div>
                  {grp.tasks.map((t) => (
                    <div key={t.id} className="h-11 border-b relative hover:bg-gray-50/60" style={{ borderColor: '#F1F5F9' }}>
                      {renderBar(t)}
                    </div>
                  ))}
                </div>
              ))}
              {groups.length === 0 && <div className="h-24" />}
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export default ProjectGantt;

